import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const FILTERS = [["", "All"], ["new", "New"], ["contacted", "Contacted"], ["closed", "Closed"]];
const TONE = { new: "text-terra-soft", contacted: "text-gold", closed: "text-cream/60" };

export function EnquiriesManager({ onChange }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");

  const load = useCallback(() => {
    api.get("/admin/enquiries", { params: status ? { status } : {} })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(errMsg(e)));
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-enquiries-manager">
      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map(([k, l]) => (
          <button key={l} onClick={() => setStatus(k)} data-testid={`enquiry-filter-${k || "all"}`}
            className={`rounded-full px-4 py-2 text-sm border transition-colors ${status === k ? "bg-cream text-ink border-cream" : "border-cream/20 text-cream/75"}`}>{l}</button>
        ))}
      </div>
      {items.length === 0 && <p className="text-cream/60" data-testid="enquiries-empty">No enquiries yet.</p>}
      <div className="space-y-3">
        {items.map((q) => (
          <div key={q.id} data-testid={`enquiry-row-${q.id}`} className="rounded-3xl bg-cream/5 border border-cream/10 p-5 grid md:grid-cols-12 gap-4 items-start">
            <div className="md:col-span-4">
              <p className="font-display text-xl font-bold">{q.name}</p>
              <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-sm text-cream/80 font-mono mt-1"><Phone className="h-3.5 w-3.5" />{q.phone}</a>
              {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-sm text-cream/80 mt-1"><Mail className="h-3.5 w-3.5" />{q.email}</a>}
            </div>
            <div className="md:col-span-5 text-sm text-cream/80 space-y-1">
              <p><span className="text-cream/60">Destination:</span> {q.destination || "–"} {q.travel_month && `· ${q.travel_month}`} {q.travellers && `· ${q.travellers} pax`}</p>
              {q.trip_title && <p><span className="text-cream/60">Trip:</span> {q.trip_title}</p>}
              {q.message && <p className="italic">"{q.message}"</p>}
              <p className="font-mono text-xs text-cream/60">{new Date(q.created_at).toLocaleString("en-IN")}
                <span className={`ml-3 ${q.notify_status === "failed" ? "text-terra-soft" : ""}`} data-testid={`enquiry-notify-${q.id}`}>
                  · email alert {q.notify_status === "sent" ? "sent" : q.notify_status === "failed" ? "failed" : "pending"}
                </span>
              </p>
            </div>
            <div className="md:col-span-3 flex items-center gap-2 md:justify-end">
              <select value={q.status} data-testid={`enquiry-status-${q.id}`}
                onChange={(e) => act(() => api.patch(`/admin/enquiries/${q.id}`, { status: e.target.value }), "Status updated")}
                className={`rounded-full bg-cream/5 border border-cream/20 px-4 py-2 text-sm outline-none ${TONE[q.status] || ""}`}>
                {["new", "contacted", "closed"].map((s) => <option key={s} value={s} className="bg-ink text-cream">{s}</option>)}
              </select>
              <button aria-label="Delete enquiry" data-testid={`enquiry-delete-${q.id}`}
                onClick={() => window.confirm("Delete this enquiry?") && act(() => api.delete(`/admin/enquiries/${q.id}`), "Enquiry deleted")}
                className="grid place-items-center h-10 w-10 rounded-full border border-cream/20 hover:bg-terra hover:border-terra transition-colors">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
